/**
 * Participant application (/apply) translations.
 * Shape mirrors src/common/Translations.js on the i18n branch: { key: {en, es, hmn, 'zh-Hans'} }.
 *
 * NOTE: the es (Spanish), hmn (Hmong Daw / White Hmong) and zh-Hans (Simplified Chinese,
 * mainland usage) strings are drafts and must be reviewed by a native speaker before
 * going to production.
 */
export const LANGUAGE_STORAGE_KEY = 'APP_LANGUAGE';
export const DEFAULT_LANGUAGE = 'en';

// flag = file name under public/img/flags
export const APPLY_LANGUAGES = [
	{ code: 'en', flag: 'us', nativeLabel: 'English' },
	{ code: 'es', flag: 'mx', nativeLabel: 'Español' },
	{ code: 'hmn', flag: 'la', nativeLabel: 'Hmoob Dawb' },
	{ code: 'zh-Hans', flag: 'cn', nativeLabel: '简体中文' },
];

const APPLY_TRANSLATIONS = {
	'apply.language': { en: 'Language', es: 'Idioma', hmn: 'Hom Lus', 'zh-Hans': '语言' },

	// Applicant fields
	'apply.firstName': { en: 'First Name', es: 'Nombre', hmn: 'Npe', 'zh-Hans': '名字' },
	'apply.middleInitial': { en: 'Middle Initial', es: 'Inicial del Segundo Nombre', hmn: 'Tus Ntawv Pib ntawm Npe Nruab Nrab', 'zh-Hans': '中间名首字母' },
	'apply.middleName': { en: 'Middle Name', es: 'Segundo Nombre', hmn: 'Npe Nruab Nrab', 'zh-Hans': '中间名' },
	'apply.lastName': { en: 'Last Name', es: 'Apellido', hmn: 'Xeem', 'zh-Hans': '姓氏' },
	'apply.address1': { en: 'Address Line One', es: 'Dirección (Línea 1)', hmn: 'Chaw Nyob Kab Ib', 'zh-Hans': '地址第一行' },
	'apply.address2': { en: 'Address Line Two', es: 'Dirección (Línea 2)', hmn: 'Chaw Nyob Kab Ob', 'zh-Hans': '地址第二行' },
	'apply.city': { en: 'City', es: 'Ciudad', hmn: 'Nroog', 'zh-Hans': '城市' },
	'apply.state': { en: 'State', es: 'Estado', hmn: 'Xeev', 'zh-Hans': '州' },
	'apply.selectOne': { en: 'Select One', es: 'Seleccione Uno', hmn: 'Xaiv Ib Qho', 'zh-Hans': '请选择' },
	'apply.zipcode': { en: 'Zip Code', es: 'Código Postal', hmn: 'Tus Lej Xa Ntawv (ZIP)', 'zh-Hans': '邮政编码 (ZIP)' },
	'apply.email': { en: 'Email', es: 'Correo Electrónico', hmn: 'Email', 'zh-Hans': '电子邮箱' },
	'apply.mobilePhone': { en: 'Mobile Phone', es: 'Teléfono Celular', hmn: 'Xov Tooj ntawm Tes', 'zh-Hans': '手机号码' },

	// Validation (translated copies of Strings.validation.*)
	'apply.zipFormat': {
		en: "Zipcode format should be '99999' or '99999-9999'.",
		es: "El código postal debe tener el formato '99999' o '99999-9999'.",
		hmn: "Tus lej xa ntawv (ZIP) yuav tsum sau li '99999' lossis '99999-9999'.",
		'zh-Hans': "邮政编码格式应为 '99999' 或 '99999-9999'。"
	},
	'apply.phoneFormat': {
		en: "Invalid phone number format. Example: '999-999-9999' or '999 999-9999' or '9999999999'.",
		es: "Formato de número de teléfono no válido. Ejemplo: '999-999-9999' o '999 999-9999' o '9999999999'.",
		hmn: "Tus xov tooj sau tsis raug. Piv txwv: '999-999-9999' lossis '999 999-9999' lossis '9999999999'.",
		'zh-Hans': "电话号码格式无效。示例：'999-999-9999'、'999 999-9999' 或 '9999999999'。"
	},
	'apply.emailFormat': {
		en: "Invalid email format. Example: 'john@mail.com'.",
		es: "Formato de correo electrónico no válido. Ejemplo: 'john@mail.com'.",
		hmn: "Email sau tsis raug. Piv txwv: 'john@mail.com'.",
		'zh-Hans': "电子邮箱格式无效。示例：'john@mail.com'。"
	},

	// Screen 1 / 2 - entry and review
	'apply.reviewApplication': { en: 'Review Application', es: 'Revisar Solicitud', hmn: 'Tshuaj Xyuas Daim Ntawv Thov', 'zh-Hans': '核对申请信息' },
	'apply.applicantInformation': { en: 'Applicant Information', es: 'Información del Solicitante', hmn: 'Cov Ntaub Ntawv ntawm Tus Neeg Thov', 'zh-Hans': '申请人信息' },
	'apply.reviewIntro': {
		en: 'Please review the information that you have provided before continuing with the application.',
		es: 'Por favor revise la información que ha proporcionado antes de continuar con la solicitud.',
		hmn: 'Thov tshuaj xyuas cov ntaub ntawv uas koj tau muab ua ntej koj mus ntxiv nrog daim ntawv thov.',
		'zh-Hans': '请在继续申请之前，核对您所填写的信息。'
	},
	'apply.editApplication': { en: 'Edit Application', es: 'Editar Solicitud', hmn: 'Kho Daim Ntawv Thov', 'zh-Hans': '修改申请信息' },
	'apply.continueApplication': { en: 'Continue With Application', es: 'Continuar con la Solicitud', hmn: 'Mus Ntxiv nrog Daim Ntawv Thov', 'zh-Hans': '继续申请' },

	// Screen 3 - terms and submit
	'apply.termsEnglishOnly': {
		en: 'The Terms of Use below are provided in English. The English version is the official, binding version.',
		es: 'Los Términos de Uso a continuación solo están disponibles en inglés. La versión en inglés es la versión oficial y vinculante.',
		hmn: 'Cov Cai Siv hauv qab no tsuas muaj ua lus Askiv xwb. Cov lus Askiv yog cov uas siv tau raws cai.',
		'zh-Hans': '以下使用条款仅提供英文版本。英文版本为正式且具有法律约束力的版本。'
	},
	'apply.agreePrefix': { en: 'I understand and agree to the ', es: 'Entiendo y acepto los ', hmn: 'Kuv nkag siab thiab pom zoo rau ', 'zh-Hans': '我理解并同意上述' },
	'apply.termsOfUse': { en: 'Terms of Use', es: 'Términos de Uso', hmn: 'Cov Cai Siv', 'zh-Hans': '使用条款' },
	'apply.agreeSuffix': { en: ' defined above.', es: ' definidos arriba.', hmn: ' uas tau piav qhia saum toj no.', 'zh-Hans': '。' },
	'apply.mustAgree': {
		en: 'You must agree to the above statements to submit the application.',
		es: 'Debe aceptar las declaraciones anteriores para enviar la solicitud.',
		hmn: 'Koj yuav tsum pom zoo rau cov lus saum toj no thiaj xa tau daim ntawv thov.',
		'zh-Hans': '您必须同意上述声明才能提交申请。'
	},
	'apply.submitApplication': { en: 'Submit Application', es: 'Enviar Solicitud', hmn: 'Xa Daim Ntawv Thov', 'zh-Hans': '提交申请' },
	'apply.backToReview': { en: 'Back To Review Application', es: 'Volver a Revisar la Solicitud', hmn: 'Rov Qab Mus Tshuaj Xyuas Daim Ntawv Thov', 'zh-Hans': '返回核对申请信息' },
	'apply.submitFailed': {
		en: 'The application cannot be submitted at this time.',
		es: 'No se puede enviar la solicitud en este momento.',
		hmn: 'Tam sim no xa tsis tau daim ntawv thov.',
		'zh-Hans': '目前无法提交申请。'
	},

	// Screen 4 - confirmation
	'apply.thankYou': { en: 'Thank You', es: 'Gracias', hmn: 'Ua Tsaug', 'zh-Hans': '谢谢' },
	'apply.received': { en: 'The application has been received.', es: 'Hemos recibido la solicitud.', hmn: 'Peb tau txais daim ntawv thov lawm.', 'zh-Hans': '我们已收到您的申请。' },
	'apply.addAnother': { en: 'Add Another Application', es: 'Agregar Otra Solicitud', hmn: 'Ntxiv Lwm Daim Ntawv Thov', 'zh-Hans': '添加另一份申请' },

	// Unavailable
	'apply.unavailable': {
		en: 'The online application is not available at this time.',
		es: 'La solicitud en línea no está disponible en este momento.',
		hmn: 'Tam sim no daim ntawv thov online tsis muaj siv.',
		'zh-Hans': '在线申请目前暂不可用。'
	},
	'apply.sorry': { en: 'Sorry for the inconvenience.', es: 'Disculpe las molestias.', hmn: 'Thov txim rau qhov tsis yooj yim.', 'zh-Hans': '给您带来不便，敬请谅解。' },
};

export function translate(key, language) {
	let entry = APPLY_TRANSLATIONS[key];
	if (!entry) return key;
	return entry[language] || entry[DEFAULT_LANGUAGE] || key;
}

// Values accepted in links (/apply/<value> or ?lang=<value>), stored normalized.
const LANGUAGE_ALIASES = {
	en: 'en', eng: 'en', english: 'en', enus: 'en',
	es: 'es', spa: 'es', spanish: 'es', espanol: 'es', esmx: 'es',
	hmn: 'hmn', mww: 'hmn', hmong: 'hmn', hmoob: 'hmn', hmongdaw: 'hmn', hmoobdawb: 'hmn', whitehmong: 'hmn',
	zh: 'zh-Hans', zhhans: 'zh-Hans', zhcn: 'zh-Hans', zhsg: 'zh-Hans', chinese: 'zh-Hans', simplifiedchinese: 'zh-Hans',
	mandarin: 'zh-Hans', '中文': 'zh-Hans', '简体中文': 'zh-Hans', '简体': 'zh-Hans', '简中': 'zh-Hans', '普通话': 'zh-Hans',
	// Traditional Chinese is not offered; its links (and saved choices) open Simplified rather than English.
	zhhant: 'zh-Hans', zhtw: 'zh-Hans', zhhk: 'zh-Hans', zhmo: 'zh-Hans', traditionalchinese: 'zh-Hans',
	'繁體中文': 'zh-Hans', '繁体中文': 'zh-Hans', '繁體': 'zh-Hans', '繁中': 'zh-Hans',
};

// Case, accents, spaces, '-' and '_' are ignored, so 'Español', 'hmoob-dawb' and 'EN_US' all match.
function normalize(value) {
	return String(value).normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[\s_-]/g, '');
}

// Returns a supported language code, or null when the value isn't recognized.
export function matchLanguage(value) {
	if (value == null) return null;
	return LANGUAGE_ALIASES[normalize(value)] || null;
}

export function saveApplyLanguage(code) {
	try {
		localStorage.setItem(LANGUAGE_STORAGE_KEY, code);
	} catch (e) {
		// localStorage unavailable (e.g. private browsing) - selection just won't persist
	}
}

// Language named in the link, from the /apply/<lang> path or else ?lang=; '' when there is none.
export function requestedLanguage(pathLang, search) {
	if (pathLang) return pathLang;
	try {
		return new URLSearchParams(search || '').get('lang') || '';
	} catch (e) {
		return '';
	}
}

// A language named in the link wins; one we don't support falls back to English, not the saved choice.
// With no language in the link, the visitor's last dropdown choice is used (mapped, so an old zh-Hant becomes zh-Hans).
export function loadApplyLanguage(pathLang, search) {
	let requested = requestedLanguage(pathLang, search);
	if (requested) return matchLanguage(requested) || DEFAULT_LANGUAGE;
	try {
		let saved = matchLanguage(localStorage.getItem(LANGUAGE_STORAGE_KEY));
		if (saved) return saved;
	} catch (e) {
		// localStorage unavailable
	}
	return DEFAULT_LANGUAGE;
}

export default APPLY_TRANSLATIONS;
